import { MODULOS } from '../../negocio/negocio.types';
import type { AlcanceNegocioAsignable, Cadena, ModuloId, Responsable } from '../../negocio/negocio.types';

/** Etiquetas de los módulos, para la pantalla Perfiles. */
export const ETIQUETA_MODULO: Record<ModuloId, string> = {
  'negocio.inicio': 'Inicio',
  'negocio.tiendas': 'Tiendas (vista de la captura)',
  'negocio.negados': 'Negados',
  presupuesto: 'Presupuesto',
  scorecard: 'Scorecard',
  'metas.ver': 'Metas: ver',
  'metas.capturar': 'Metas: capturar',
  'metas.autorizar': 'Metas: autorizar',
  'finanzas.utilidad': 'Utilidad y margen',
  'sistemas.salud': 'Salud del ERP y hora de las copias',
};

/** Grupos de la pantalla. Cada módulo del contrato está en uno y sólo uno (hay prueba). */
export const GRUPOS_MODULOS: Array<{ titulo: string; modulos: ModuloId[] }> = [
  { titulo: 'Consulta CM', modulos: ['negocio.inicio', 'negocio.tiendas', 'negocio.negados'] },
  { titulo: 'Tableros', modulos: ['presupuesto', 'scorecard'] },
  { titulo: 'Metas por colaborador', modulos: ['metas.ver', 'metas.capturar', 'metas.autorizar'] },
  { titulo: 'Datos sensibles', modulos: ['finanzas.utilidad'] },
  { titulo: 'Sistemas', modulos: ['sistemas.salud'] },
];

/**
 * Nunca vienen implícitos. La utilidad, además del módulo, exige la llave
 * maestra `config.negocio.utilidad.habilitada`, que hoy está apagada para
 * todos hasta que Finanzas valide el costo.
 */
export const MODULOS_SENSIBLES: ModuloId[] = ['finanzas.utilidad'];

export function ordenarModulos(ms: readonly string[]): ModuloId[] {
  const set = new Set(ms);
  return MODULOS.filter((m) => set.has(m));
}

/** La misma regla que la API (`CLAVE_INVALIDA`). */
export const CLAVE_PERFIL_RE = /^[a-z0-9_-]{2,32}$/;

export function validarPerfil(p: { clave: string; nombre: string; modulos: readonly string[] }, creando: boolean): string[] {
  const errores: string[] = [];
  if (creando && !CLAVE_PERFIL_RE.test(p.clave)) {
    errores.push('La clave sólo admite minúsculas, números, «-» y «_» (de 2 a 32).');
  }
  if (!p.nombre.trim()) errores.push('Falta el nombre.');
  if (ordenarModulos(p.modulos).length === 0) errores.push('Elija al menos un módulo.');
  return errores;
}

// ───────────────────────── Alcances ─────────────────────────

export const CADENAS: Cadena[] = ['TRE', 'VAZ', 'TAF', 'MRH'];

export interface OpcionTienda {
  label: string;
  value: { cadena: Cadena; num: number };
}

const porTexto = (a: string, b: string) => a.localeCompare(b, 'es');

/**
 * Nombres y tiendas que se pueden asignar, tomados de los responsables
 * vigentes (`GET /tiendas/responsables`), que es contra lo que valida la API
 * (`NOMBRE_DESCONOCIDO`, `TIENDA_DESCONOCIDA`).
 */
export function opcionesDeResponsables(lista: Responsable[]): {
  supervisores: string[];
  regionales: string[];
  tiendas: OpcionTienda[];
} {
  const sup = new Set<string>();
  const reg = new Set<string>();
  for (const r of lista) {
    if (r.supervisor) sup.add(r.supervisor);
    if (r.regional) reg.add(r.regional);
  }
  const tiendas = lista
    .map((r) => ({ label: `${r.cadena} ${r.num} · ${r.tienda}`, value: { cadena: r.cadena, num: r.num } }))
    .sort((a, b) => porTexto(a.value.cadena, b.value.cadena) || a.value.num - b.value.num);
  return { supervisores: Array.from(sup).sort(porTexto), regionales: Array.from(reg).sort(porTexto), tiendas };
}

export function mismoAlcance(a: AlcanceNegocioAsignable, b: AlcanceNegocioAsignable): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** «Todas» ya lo cubre todo: con ella no se agrega nada más, y al agregarla se va lo demás. */
export function agregarAlcance(lista: AlcanceNegocioAsignable[], nuevo: AlcanceNegocioAsignable): AlcanceNegocioAsignable[] {
  if (nuevo.tipo === 'todas') return [{ tipo: 'todas' }];
  const sinTodas = lista.filter((a) => a.tipo !== 'todas');
  return sinTodas.some((a) => mismoAlcance(a, nuevo)) ? sinTodas : [...sinTodas, nuevo];
}

export const quitarAlcance = (lista: AlcanceNegocioAsignable[], i: number) => lista.filter((_, j) => j !== i);
